/**
 * Standalone Quick View Modal Handler
 * Handles pin code checking and add to cart button state in quick view modals
 * Does not modify existing code - works independently
 */

(function() {
  'use strict';
  
  // Configuration
  var config = {
    checkInterval: 100,
    maxCheckAttempts: 50,
    pinCodeCheckDelay: 300
  };
  
  var QuickViewModalHandler = {
    initialized: false,
    activeModals: {},
    
    /**
     * Initialize the handler
     */
    init: function() {
      if (this.initialized) return;
      this.initialized = true;
      
      // Listen for modal opens
      document.addEventListener('modalOpen', this.handleModalOpen.bind(this));
      document.addEventListener('quickview:loaded', this.handleQuickViewLoaded.bind(this));
      
      // Also check for existing modals
      this.checkExistingModals();
    },
    
    /**
     * Check for existing modals on page load
     */
    checkExistingModals: function() {
      var modals = document.querySelectorAll('.modal--quick-shop');
      modals.forEach(function(modal) {
        this.setupModal(modal);
      }.bind(this));
    },
    
    /**
     * Handle modal open event
     */
    handleModalOpen: function(event) {
      // Try multiple ways to find the modal
      var modal = null;
      
      // Method 1: From event detail
      if (event.detail && event.detail.modalId) {
        modal = document.getElementById(event.detail.modalId);
      }
      
      // Method 2: Find active quick shop modal
      if (!modal) {
        modal = document.querySelector('.modal--quick-shop.modal--is-active');
      }
      
      // Method 3: Find any open quick shop modal
      if (!modal) {
        var modals = document.querySelectorAll('.modal--quick-shop');
        modals.forEach(function(m) {
          if (m.classList.contains('modal--is-active') || m.style.display !== 'none') {
            modal = m;
          }
        });
      }
      
      if (modal) {
        // Reset active state to allow re-setup
        delete this.activeModals[modal.id];
        this.setupModal(modal);
      }
    },
    
    /**
     * Handle quickview loaded event
     */
    handleQuickViewLoaded: function(event) {
      setTimeout(function() {
        var modals = document.querySelectorAll('.modal--quick-shop');
        modals.forEach(function(modal) {
          this.setupModal(modal);
        }.bind(this));
      }.bind(this), config.pinCodeCheckDelay);
    },
    
    /**
     * Setup pin code checker and button state for a modal
     */
    setupModal: function(modal) {
      if (!modal || !modal.id) return;
      
      var modalId = modal.id;
      
      // Wait for form to load
      this.waitForForm(modal, function() {
        this.disableAddToCartButtons(modal);
        this.setupPinCodeChecker(modal);
        this.activeModals[modalId] = true;
      }.bind(this));
    },
    
    /**
     * Wait for product form to load in modal
     */
    waitForForm: function(modal, callback) {
      var attempts = 0;
      var checkForm = function() {
        attempts++;
        var form = modal.querySelector('form.product-single__form');
        var formHolder = modal.querySelector('[data-blocks-holder]');
        var hasFormContent = form && form.querySelector('button.add-to-cart');
        var hasLoadedClass = formHolder && formHolder.classList.contains('product-form-holder--loaded');
        var hasFormInHolder = formHolder && formHolder.querySelector('form');
        
        // Check if form is loaded (either form exists with button or formHolder has loaded class)
        if (hasFormContent || hasLoadedClass || hasFormInHolder) {
          // Wait a bit more for form to be fully rendered
          setTimeout(callback, 200);
          return;
        }
        
        if (attempts < config.maxCheckAttempts) {
          setTimeout(checkForm, config.checkInterval);
        } else {
          // Even if form not found, setup pin code checker anyway
          setTimeout(callback, 100);
        }
      };
      
      checkForm();
    },
    
    /**
     * Disable all add to cart buttons in modal
     */
    disableAddToCartButtons: function(modal) {
      var buttons = modal.querySelectorAll('button.add-to-cart[data-add-to-cart]');
      buttons.forEach(function(button) {
        // Only disable if variant is available (don't override sold out state)
        if (!button.disabled || button.getAttribute('disabled') !== 'disabled') {
          button.setAttribute('data-quickview-requires-pincode', 'true');
          button.disabled = true;
        }
      });
    },
    
    /**
     * Setup pin code checker for modal
     */
    setupPinCodeChecker: function(modal) {
      var modalId = modal.id;
      
      // Always create pin code checker (will skip if already exists)
      var checkerContainer = modal.querySelector('.quick-view-code-checker');
      if (!checkerContainer) {
        this.createPinCodeChecker(modal);
        checkerContainer = modal.querySelector('.quick-view-code-checker');
      }
      
      if (!checkerContainer) {
        console.warn('QuickViewModalHandler: Could not create pin code checker');
        return;
      }
      
      // Get postal codes from page
      var postalCodes = this.getPostalCodes();
      
      if (postalCodes.length === 0) {
        console.warn('QuickViewModalHandler: No postal codes found on page');
        // Still show checker, but it won't work until codes are available
      }
      
      // Find check button
      var checkButton = checkerContainer.querySelector('button[data-quickview-check-pincode]');
      var input = checkerContainer.querySelector('input[data-quickview-pincode-input]');
      var messageDiv = checkerContainer.querySelector('[data-quickview-pincode-message]');
      
      if (!checkButton || !input || !messageDiv) {
        console.warn('QuickViewModalHandler: Pin code checker elements not found');
        return;
      }
      
      // Remove existing listeners by cloning
      var newCheckButton = checkButton.cloneNode(true);
      checkButton.parentNode.replaceChild(newCheckButton, checkButton);
      checkButton = newCheckButton;
      
      // Store postal codes in closure
      var self = this;
      
      // Add click handler
      checkButton.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        // Re-fetch postal codes in case they were loaded dynamically
        var codes = self.getPostalCodes();
        self.checkPinCode(modal, input.value.trim(), codes, messageDiv);
      });
      
      // Handle Enter key
      input.addEventListener('keypress', function(e) {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          checkButton.click();
        }
      });
      
      // Also allow input validation on blur
      input.addEventListener('input', function() {
        // Only allow numbers
        this.value = this.value.replace(/[^0-9]/g, '');
        if (this.value.length > 6) {
          this.value = this.value.substring(0, 6);
        }
      });
    },
    
    /**
     * Create pin code checker UI in modal
     */
    createPinCodeChecker: function(modal) {
      // Find where to insert (before form or at end of modal content)
      var formHolder = modal.querySelector('[data-blocks-holder]');
      var productSection = modal.querySelector('.product-section');
      var modalContent = modal.querySelector('.modal__centered-content');
      
      var insertPoint = null;
      
      // Try to find best insertion point
      if (formHolder && formHolder.parentNode) {
        insertPoint = formHolder.parentNode;
      } else if (productSection) {
        insertPoint = productSection;
      } else if (modalContent) {
        insertPoint = modalContent;
      }
      
      if (!insertPoint) {
        console.warn('QuickViewModalHandler: Could not find insertion point for pin code checker');
        return;
      }
      
      var checkerHtml = '<div class="quick-view-code-checker" style="margin: 20px 0; padding: 0 20px;">' +
        '<p style="margin-bottom: 10px; font-weight: 500; font-size: 14px;">Check availability</p>' +
        '<div style="display: flex; gap: 10px; margin-bottom: 10px; align-items: center;">' +
        '<input type="text" data-quickview-pincode-input placeholder="Enter 6-digit pincode" maxlength="6" pattern="[0-9]{6}" style="flex: 1; padding: 10px; border: 1px solid #ccc; border-radius: 4px; font-size: 14px;">' +
        '<button type="button" data-quickview-check-pincode class="btn" style="padding: 10px 20px; white-space: nowrap; font-size: 14px;">Check</button>' +
        '</div>' +
        '<div data-quickview-pincode-message style="display: none; margin-top: 10px; font-size: 14px; min-height: 20px;"></div>' +
        '</div>';
      
      var tempDiv = document.createElement('div');
      tempDiv.innerHTML = checkerHtml;
      var checkerElement = tempDiv.firstElementChild;
      
      // Insert before form holder if it exists, otherwise append
      if (formHolder && formHolder.parentNode === insertPoint) {
        insertPoint.insertBefore(checkerElement, formHolder);
      } else {
        insertPoint.appendChild(checkerElement);
      }
    },
    
    /**
     * Get postal codes from page
     */
    getPostalCodes: function() {
      var codes = [];
      // Try multiple selectors to find postal codes
      var selectors = [
        '.postal .postalcode',
        '.postal-code .postalcode',
        '[class*="postal"] .postalcode',
        'ul.postal-codes .postalcode',
        'ul.postal-codes li'
      ];
      
      for (var i = 0; i < selectors.length; i++) {
        var postalElements = document.querySelectorAll(selectors[i]);
        if (postalElements.length > 0) {
          postalElements.forEach(function(el) {
            var code = el.textContent.trim();
            // Remove any whitespace and ensure it's a valid code
            code = code.replace(/\s+/g, '');
            if (code && /^\d+$/.test(code)) {
              codes.push(code);
            }
          });
          break; // Found codes, stop searching
        }
      }
      
      return codes;
    },
    
    /**
     * Check pin code and update button state
     */
    checkPinCode: function(modal, pinCode, postalCodes, messageDiv) {
      // Validate input
      if (!pinCode || pinCode.length !== 6 || !/^\d{6}$/.test(pinCode)) {
        this.showMessage(messageDiv, 'Please enter a valid 6-digit pincode', '#ff4444');
        this.disableAddToCartButtons(modal);
        return;
      }
      
      // Normalize pin code (remove spaces)
      pinCode = pinCode.trim().replace(/\s+/g, '');
      
      // Check if pin code is in list (case-insensitive comparison)
      var isValid = false;
      for (var i = 0; i < postalCodes.length; i++) {
        var code = postalCodes[i].trim().replace(/\s+/g, '');
        if (code === pinCode) {
          isValid = true;
          break;
        }
      }
      
      if (isValid) {
        this.showMessage(messageDiv, '✓ Delivery available in your area', '#00cc44');
        this.enableAddToCartButtons(modal);
      } else {
        this.showMessage(messageDiv, '✗ Sorry, delivery is not available in your area', '#ff4444');
        this.disableAddToCartButtons(modal);
      }
    },
    
    /**
     * Show message
     */
    showMessage: function(messageDiv, text, color) {
      messageDiv.style.display = 'block';
      messageDiv.style.color = color;
      messageDiv.textContent = text;
    },
    
    /**
     * Enable add to cart buttons in modal
     */
    enableAddToCartButtons: function(modal) {
      var buttons = modal.querySelectorAll('button.add-to-cart[data-add-to-cart]');
      buttons.forEach(function(button) {
        if (button.getAttribute('data-quickview-requires-pincode') === 'true') {
          button.removeAttribute('data-quickview-requires-pincode');
          button.disabled = false;
        }
      });
    }
  };
  
  // Initialize when DOM is ready
  function initializeHandler() {
    QuickViewModalHandler.init();
    
    // Also check for modals that might already be open
    setTimeout(function() {
      var modals = document.querySelectorAll('.modal--quick-shop');
      modals.forEach(function(modal) {
        if (modal.classList.contains('modal--is-active') || 
            window.getComputedStyle(modal).display !== 'none') {
          QuickViewModalHandler.setupModal(modal);
        }
      });
    }, 500);
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeHandler);
  } else {
    // Wait a bit for other scripts to load
    setTimeout(initializeHandler, 100);
  }
  
  // Also listen for dynamically added modals
  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            var modal = null;
            
            // Check if node itself is a modal
            if (node.classList && node.classList.contains('modal--quick-shop')) {
              modal = node;
            } else if (node.querySelector) {
              // Check for modal in children
              modal = node.querySelector('.modal--quick-shop');
            }
            
            if (modal) {
              // Reset active state to allow setup
              delete QuickViewModalHandler.activeModals[modal.id];
              QuickViewModalHandler.setupModal(modal);
            }
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  // Also listen for modal class changes (when modal opens)
  if (typeof MutationObserver !== 'undefined') {
    var classObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          var target = mutation.target;
          if (target.classList && target.classList.contains('modal--quick-shop')) {
            if (target.classList.contains('modal--is-active')) {
              // Modal just opened
              delete QuickViewModalHandler.activeModals[target.id];
              QuickViewModalHandler.setupModal(target);
            }
          }
        }
      });
    });
    
    // Observe all modals for class changes
    setTimeout(function() {
      var modals = document.querySelectorAll('.modal--quick-shop');
      modals.forEach(function(modal) {
        classObserver.observe(modal, {
          attributes: true,
          attributeFilter: ['class']
        });
      });
    }, 1000);
  }
  
  // Expose for manual triggering if needed
  window.QuickViewModalHandler = QuickViewModalHandler;
  
  // Periodic check for open modals (fallback)
  setInterval(function() {
    var openModals = document.querySelectorAll('.modal--quick-shop.modal--is-active');
    openModals.forEach(function(modal) {
      if (!QuickViewModalHandler.activeModals[modal.id]) {
        QuickViewModalHandler.setupModal(modal);
      }
    });
  }, 1000);
})();

